import { drawnFrameAnchor, type ManifestObjectAppearance } from "daedalus-core";
import { SIXTEENTHS } from "../movement/step.ts";

/**
 * Where a client draws a thing (P1.1-T7, ADR-071 known difference 1): at its own prop layer's place in the
 * draw order, with its sort offset, at the author's pixel, as the editor draws the prop it was lowered from.
 * The image follows the appearance's `layer`, `sortPixels` and `offset`; what the thing blocks and where it is
 * usable from are laid on the collision cells from the same drawn frame (`drawnFrameAnchor`, ADR-040 amendment
 * of 2026-10-04, P1.6-T83), so they stay under the art.
 */

/**
 * A thing's drawn placement: the compiled index of the layer it draws at, its anchor in tiles, its sort offset
 * and its tie key in its layer (P1.6-T42).
 */
export interface ThingPlace {
  readonly layer: number;
  readonly x: number;
  readonly y: number;
  readonly sortPixels: number;
  readonly order: number;
}

/**
 * The placement a thing draws at. An appearance written without the fields draws at the band's lowest layer
 * with no sort offset, on the anchor of the feet point, as every thing drew before they existed, and one
 * written without a tie key draws with key 0.
 *
 * @param look The thing's appearance.
 * @param frameWidth The pixel width of the frame it shows now.
 * @param feet The feet point in sixteenths of a tile, as the snapshot carries it.
 * @param tileSize Tile size in pixels.
 * @param band The Players band, the compiled index of its lowest layer.
 * @returns The placement.
 */
export function thingPlace(look: ManifestObjectAppearance, frameWidth: number, feet: { readonly x: number; readonly y: number }, tileSize: number, band: number): ThingPlace {
  const anchor = drawnFrameAnchor({ x: feet.x / SIXTEENTHS, y: feet.y / SIXTEENTHS }, frameWidth, look.offset, tileSize);
  return {
    layer: look.layer ?? band,
    x: anchor.x,
    y: anchor.y,
    sortPixels: look.sortPixels ?? 0,
    order: look.order ?? 0,
  };
}
